import type { PublicPluginAPI, OptionalPlugin } from '@atlaskit/editor-common/types';
import { fg } from '@atlaskit/platform-feature-flags';
import type { MalleableUiPlugin } from '@atlassian/editor-plugin-malleable-ui';
import type { ConfluenceUpdateImageOnPageData } from '@atlassian/rovo-content-bridge-api-commands/commands/confluence-update-page-command';
import { remixRegenerateExperience } from '@atlassian/rovo-content-bridge-api-experience-tracking-sender/remix-regenerate-experience';

import type { PassThroughCommandProps } from '../command-handlers/command-handler';

export class DocumentImageUpdateCommandProcessor {
	private onUploadAndInsertMedia?: PassThroughCommandProps['onUploadAndInsertMedia'];
	private onPrepareImageUpdate?: PassThroughCommandProps['onPrepareImageUpdate'];
	private onClearAIGenerating?: PassThroughCommandProps['onClearAIGenerating'];
	private editorAPI?: PublicPluginAPI<[OptionalPlugin<MalleableUiPlugin>]>;
	private hasPrepared = false;
	private lastPreviousMediaFileId?: string;

	constructor(
		onUploadAndInsertMedia: PassThroughCommandProps['onUploadAndInsertMedia'],
		onPrepareImageUpdate?: PassThroughCommandProps['onPrepareImageUpdate'],
		onClearAIGenerating?: PassThroughCommandProps['onClearAIGenerating'],
		editorAPI?: PublicPluginAPI<[OptionalPlugin<MalleableUiPlugin>]>,
	) {
		this.onUploadAndInsertMedia = onUploadAndInsertMedia;
		this.onPrepareImageUpdate = onPrepareImageUpdate;
		this.onClearAIGenerating = onClearAIGenerating;
		this.editorAPI = editorAPI;
	}

	updateCallback(onUploadAndInsertMedia: PassThroughCommandProps['onUploadAndInsertMedia']): void {
		this.onUploadAndInsertMedia = onUploadAndInsertMedia;
	}

	updatePrepareCallback(
		onPrepareImageUpdate: PassThroughCommandProps['onPrepareImageUpdate'],
	): void {
		this.onPrepareImageUpdate = onPrepareImageUpdate;
	}

	updateClearCallback(onClearAIGenerating: PassThroughCommandProps['onClearAIGenerating']): void {
		this.onClearAIGenerating = onClearAIGenerating;
	}

	streamChunk(
		data: ConfluenceUpdateImageOnPageData,
		isFinished: boolean,
		_traceId?: string,
		_singleInstrumentationId?: string,
	): void {
		// If the stream errored, clear the AI generating state and bail out
		if (
			'__isError' in data &&
			(data as ConfluenceUpdateImageOnPageData & { __isError?: boolean }).__isError
		) {
			this.streamError();
			return;
		}

		// A safety / AUP violation is streamed as a finished ACTION_COMPLETE with
		// `isSafetyViolation: true` and no media. This is an expected outcome, not
		// a system failure, so it must not publish a `taskFail` operational metric
		// (which is pure noise). Clear the AI generating state, publish the outcome
		// as a success, and skip the (impossible) media insertion.
		if (isFinished && data.isSafetyViolation) {
			if (this.lastPreviousMediaFileId) {
				this.onClearAIGenerating?.(this.lastPreviousMediaFileId);
				this.lastPreviousMediaFileId = undefined;
			}
			this.hasPrepared = false;
			remixRegenerateExperience.success();
			return;
		}

		// Variant-picker image updates are handled FE-side (useUpdateRemix ->
		// useOnComplete uploads + inserts and drives the loading state); the BE
		// push here would double both. Rovo-chat updates aren't tracked, so they
		// still flow through.
		if (
			data.previousMediaFileId &&
			this.editorAPI?.malleableUi?.actions.isVariantUpdateInFlight(data.previousMediaFileId) &&
			fg('platform_editor_ai_remix_toolbar')
		) {
			if (isFinished) {
				// Surface success so the experience tracker closes normally.
				remixRegenerateExperience.success();
			}
			return;
		}

		// On the first chunk, if there's a previousMediaFileId, trigger the loading
		// state on the existing image immediately (before the upload starts)
		if (!this.hasPrepared && data.previousMediaFileId) {
			this.hasPrepared = true;
			this.lastPreviousMediaFileId = data.previousMediaFileId;
			this.onPrepareImageUpdate?.(data.previousMediaFileId);
		}

		if (isFinished) {
			this.hasPrepared = false;
			this.lastPreviousMediaFileId = undefined;
			void this.onUploadAndInsertMedia?.(data.mediaUrl, data)
				?.then((result) => {
					remixRegenerateExperience.success();
					// `result.fieldId` is the upload-side upfront id assigned by
					// mediaClient and written onto mediaNode.attrs.id; the BE-side
					// `data.mediaFileId` is a different identifier and won't match
					// the live node when the toolbar looks up the stack entry.
					const liveFieldId = result?.fieldId;
					if (data.previousMediaFileId && liveFieldId && fg('platform_editor_ai_remix_toolbar')) {
						// `dynamicUiSubtype` is intentionally omitted: the reducer inherits
						// it from the matched entry (the backend's echoed value is
						// unreliable for edits that aren't told the current subtype).
						const cmd = this.editorAPI?.malleableUi?.commands.addBackendRemixHistoryStackEntry({
							previousFieldId: data.previousMediaFileId,
							fieldId: liveFieldId,
						});
						if (cmd) {
							this.editorAPI?.core?.actions.execute(cmd);
						}
					}
				})
				?.catch((error: unknown) => {
					remixRegenerateExperience.fail({
						error: error instanceof Error ? error.message : 'upload_and_insert_failed',
					});
				});
		}
	}

	/**
	 * Clears editor-side loading state for image updates. This is safe even before
	 * image prepare data arrives, which can happen when Rovo chat blocks a request.
	 */
	clearStreamingState(): void {
		const editorAPI = this.editorAPI as PassThroughCommandProps['editorAPI'] | undefined;
		editorAPI?.aiExperience?.actions.endInlineRovoStreaming?.();
		editorAPI?.aiExperience?.actions.toggleAiEntryPoints?.(false);
		if (this.lastPreviousMediaFileId) {
			this.onClearAIGenerating?.(this.lastPreviousMediaFileId);
			this.lastPreviousMediaFileId = undefined;
		}
		this.hasPrepared = false;
	}

	/**
	 * Called when the SSE stream errors (ACTION_ERROR) to clear the AI generating
	 * state from the existing image node.
	 */
	streamError(): void {
		if (this.lastPreviousMediaFileId) {
			this.onClearAIGenerating?.(this.lastPreviousMediaFileId);
			this.lastPreviousMediaFileId = undefined;
		}
		this.hasPrepared = false;
		remixRegenerateExperience.fail({ error: 'stream_error' });
	}

	/**
	 * Called on abort when ACTION_PREPARE was received, matching the legacy image
	 * abort behavior.
	 */
	abortIfPrepared(): void {
		if (this.hasPrepared) {
			this.streamError();
		}
	}

	/**
	 * Called on abort to clear any editor-side image loading state. When ACTION_PREPARE
	 * was received, also fail the remix experience before clearing state.
	 */
	abortAndClearStreamingState(): void {
		if (this.hasPrepared) {
			remixRegenerateExperience.fail({ error: 'stream_error' });
		}
		this.clearStreamingState();
	}
}
